import {html} from 'lit-html';
import {ifDefined} from 'lit-html/directives/if-defined';
import {live} from 'lit-html/directives/live';
import {classMap} from 'lit-html/directives/class-map';

import TemplateRegistry from '../template/template-registry';
import ResourceLoader from '../resource-loader';
import Translate from '../translate';
import {
    inputDateSupported, optionClassMap, optionName
} from '../template/template-util.js';

class DateController {

    constructor(onChange, getOption) {
        this.onChange = onChange;
        this.getOption = getOption;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render
    }

    setup() {

        this.native = inputDateSupported();

        // check if native date input is supported
        if(this.native){
            this.ready = Promise.resolve();
        } else {
            // load polyfill plugin async
            let resources = [
                 this.config.assets + '/static/lib/dateinput/dateinput.min.js'
            ];

            // load resources if not already
            this.ready = ResourceLoader.loadOnce(resources, 'option-date',  {async: false /* fetch parallel - load in series */ });
        }


    }

    onValidate() {
        // native date field validation
        let optionEl = this.dateEl;

        let valid = optionEl.checkValidity();

        // polyfill manual min/max validation
        let rangeUnderFlow = false;
        let rangeOverflow = false;
        if(valid && !this.native){
            if(optionEl.min){
                let dateMin = new Date(optionEl.min + 'T00:00:00');
                if(optionEl.valueAsDate < dateMin){
                    valid = false;
                    rangeUnderFlow = true;
                }
            }
            if(optionEl.max){
                let dateMax = new Date(optionEl.max + 'T00:00:00');
                if(optionEl.valueAsDate > dateMax){
                    valid = false;
                    rangeOverflow = true;
                }
            }
        }

        if(!valid){

            // date min/max validation
            if(optionEl.validity.rangeUnderflow || rangeUnderFlow){
                let min = optionEl.min;
                let dateMin = new Date(min + 'T00:00:00');
                dateMin.setDate(dateMin.getDate() - 1); // remove one day for the message to make sense
                // format error message in specific date format
                min = this.format(dateMin, this.option.params.dateFormat || 'yyyy-mm-dd');
                this.option.errors.push(Translate.get('product.options.validity.dateMin', { min: min }));
            } else if(optionEl.validity.rangeOverflow || rangeOverflow){
                let max = optionEl.max;
                let dateMax = new Date(max + 'T00:00:00');
                dateMax.setDate(dateMax.getDate() + 1); // add one day for the message to make sense
                // format error message in specific date format
                max = this.format(dateMax, this.option.params.dateFormat || 'yyyy-mm-dd');
                this.option.errors.push(Translate.get('product.options.validity.dateMax', { max: max }));
            } else {
                // fallback required
                this.option.errors.push(Translate.get('product.options.validity.valueMissing'));
            }

            return false;
        }
        return true;
    }

    handleChange(e, option) { // IMPORTANT: to pass option and not use the reference of the ['date'] = (option, ..)   in editorMode the option reference can change
        this.mapDateValue(option);
        // trigger change
        this.onChange(option);
    }

    mapDateValue(option) {
        if(this.dateEl.value){

            if(option.params.dateFormat){
                // NEW: valueAsDate does assume dateEl is in UTC and converts the date to the local timezone
                let date = this.dateEl.valueAsDate;
                // we re-convert to UTC to get the actually selected date from the picker
                let dateUTC = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
                // format with the selected date
                this.el.value = this.format(dateUTC, option.params.dateFormat);
            } else {
                // default format is yyyy-mm-dd
                this.el.value = this.dateEl.value;
            }

        } else {
            this.el.value = '';
        }

        // syn with option model
        option.value = this.el.value;
    }

    mapOptionValue() {
        // check if option value changed from outside (required for cart restore support)
        if(this.option.value && this.option.value !== this.el.value){ // INFO: el element has live(option.value) mapping but this expression is not yet updated
            let date;
            let dateFormat = this.option.params.dateFormat;
            // parse value in date format
            if(dateFormat && dateFormat !== 'yyyy-mm-dd'){
                let regex; // hardcoded regex formats (this does not catch all variants..)
                if(dateFormat.indexOf('/') !== -1){
                    if(dateFormat.startsWith('yyyy')){
                        regex = /(?<year>\d{4})\/(?<month>\d{2})\/(?<day>\d{2})/;
                    } else {
                        regex = /(?<day>\d{2})\/(?<month>\d{2})\/(?<year>\d{4})/;
                    }
                } else if(dateFormat.indexOf('.') !== -1){
                    if(dateFormat.startsWith('dd')){
                        regex = /(?<day>\d{2})\.(?<month>\d{2})\.(?<year>\d{4})/;
                    } else {
                        regex = /(?<month>\d{2})\.(?<day>\d{2})\.(?<year>\d{4})/;
                    }
                }

                if(regex){
                    let match = regex.exec(this.option.value);
                    if(match){
                        date = new Date(match.groups.year, match.groups.month - 1, match.groups.day);
                    }
                }

            } else {
                // default format is yyyy-mm-dd
                date = new Date(this.option.value + 'T00:00:00');
            }

            if(date){
                // set date el value as date
                this.dateEl.valueAsDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())); // UTC required

                return true; // updated
            }
        }

        return false;
        // INFO: this.el.value is updated via live(option.value)
    }

    format(date, format) {

        // NEW: custom month name in browser locale:  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleString#using_options
        if(format.indexOf('mmmm') !== -1){
            format = format.replace('mmmm', date.toLocaleString('default', { month: 'long' }));
        } else if(format.indexOf('mmm') !== -1){
            format = format.replace('mmm', date.toLocaleString('default', { month: 'short' }));
        }

        //yyyy-mm-dd
        format = format.replace('yyyy', date.getFullYear());
        format = format.replace('yy', date.getFullYear().toString().substr(-2));
        format = format.replace('mm', ('0' + (date.getMonth() + 1)).slice(-2));
        format = format.replace('dd', ('0' + (date.getDate())).slice(-2));
        return format;
    }

    init(option, options) {

        // store options reference
        if(options){
            this.options = options;
        }

        // wait for resources to be ready before init
        this.ready.then(() => {

            // get hidden element
            this.el = document.querySelector('#' + option.id);
            // get date element
            this.dateEl = document.querySelector('#' + option.id + '-date');

            // polyfill settings
            if(!this.native){
                // init language and week start
                if(window.navigator && window.navigator.language){
                    let language = navigator.language;
                    let lang = language.substr(0,2);

                    // set full lang (polyfill uses substring to find translations)
                    this.dateEl.setAttribute('lang', language);

                    // simple check if not english set first day as monday (europe)
                    if(lang !== 'en'){
                        this.dateEl.setAttribute('data-first-day', 'mo');
                    }
                }
                if(option.params.dateFormat) {
                    this.dateEl.setAttribute('data-date-format', option.params.dateFormat);
                }
            }

            // map initial option.value if provided
            this.mapOptionValue();

            // setup min/max constraints
            this.initMinMax(option);

        });

    }

    initMinMax(option) {

        const maxDays = (date) => {
            date.setDate(date.getDate() + option.params.maxDays);

            // if maxDate is set as well avoid going above this date
            if(option.params.maxDate){
                let dateMax = new Date(option.params.maxDate + 'T00:00:00');
                if(date > dateMax){
                    date = dateMax;
                }
            }

            // directly set max attribute
            this.dateEl.max = this.format(date, 'yyyy-mm-dd');

            // update if current date is bigger than max
            if(this.dateEl.valueAsDate && this.dateEl.valueAsDate > date){
                this.dateEl.valueAsDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())); // required to set as UTC date (polyfill supports - required for custom formats)
                this.mapDateValue(option);
            }
        }
        const minDays = (date) => {
            date.setDate(date.getDate() + option.params.minDays);

            // if minDate is set as well avoid going below this date
            if(option.params.minDate){
                let dateMin = new Date(option.params.minDate + 'T00:00:00');
                if(date < dateMin){
                    date = dateMin;
                }
            }

            // directly set min attribute
            this.dateEl.min = this.format(date, 'yyyy-mm-dd');
            // update option value if empty OR if current date is smaller than min
            if(!option.value || (this.dateEl.valueAsDate && this.dateEl.valueAsDate < date)){
                this.dateEl.valueAsDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())); // required to set as UTC date (polyfill supports - required for custom formats)
                this.mapDateValue(option);
            }
        }

        const hasMaxDays = !isNaN(option.params.maxDays) && option.params.maxDays != null;
        const hasMinDays = !isNaN(option.params.minDays) && option.params.minDays != null;

        if(hasMinDays || hasMaxDays){

            const updateDays = (date) => {
                if(hasMaxDays){
                    maxDays(new Date(date.getTime())); // IMPORTANT: new Date() to avoid modifying the reference
                }
                if(hasMinDays){
                    minDays(new Date(date.getTime()));  // IMPORTANT: new Date() to avoid modifying the reference
                }
            }

            let today = new Date(); // default init from today

            // update based on other option
            if(option.params.optionDate){

                // init directly by reference (no nested reference support)
                if(this.options){
                    for (let i = 0; i < this.options.length; i++) {
                        const optionDate = this.options[i];
                        if(optionDate.name === option.params.optionDate && optionDate._controller && optionDate._controller.dateEl){
                            today = optionDate._controller.dateEl.valueAsDate;
                            break;
                        }
                    }
                }

                // editorMode: de-register previous event handler
                if(this.optionChanged){
                    this.config.productForm.removeEventListener('option:changed', this.optionChanged);
                }

                this.optionChanged = (e) => {
                    let optionDate = e.detail;
                    // check if referenced option changed & date controller contains date
                    if(optionDate.name === option.params.optionDate && optionDate._controller && optionDate._controller.dateEl){
                        // update min/max days with date from other option value
                        updateDays(optionDate._controller.dateEl.valueAsDate);
                        this.onChange(option); // manual change trigger (in case other date options are dependent on this option)
                    }
                };

                // listen to all option changes
                this.config.productForm.addEventListener('option:changed', this.optionChanged);
            }

            // init days
            updateDays(today);
        }
        // pre select min date if provided
        else if(this.dateEl.min){
            let dateMin = new Date(this.dateEl.min + 'T00:00:00');

            if(!option.value || (this.dateEl.valueAsDate && this.dateEl.valueAsDate < dateMin)){
                this.dateEl.valueAsDate = new Date(Date.UTC(dateMin.getFullYear(), dateMin.getMonth(), dateMin.getDate())); // required to set as UTC date (polyfill supports - required for custom formats)
                this.mapDateValue(option);
            }

        }
    }
}

TemplateRegistry.templates['date'] = (option, config, onChange) => {

    // init flag when no controller yet
    let init = option._controller === undefined;

    // get or create date controller
    option._controller = option._controller || new DateController(onChange);
    option._controller.config = config;
    option._controller.option = option;

    // setup date instance
    if(config.productForm){
        if(init){

            // load resources
            option._controller.setup();

            // rendered event has reference to ProductOptions.js
            const initController = (e) => {
                let productOptions = e.detail;
                // remove init event
                config.productForm.removeEventListener('options:rendered', initController);
                // call init
                option._controller.init(option, productOptions.config.options); // pass options for optionDate reference
            };
            // wait for options render to complete ( could be solved better by support custom javascript post render execution by option ( maybe define on _controller.postRender() )
            config.productForm.addEventListener('options:rendered', initController);
        } else {
            // INFO: editor mode always re-init the values (updates all the settings)
            if(config.editorMode){
                option._controller.init(option);
            } else {
                // map option value if date value changed (cart restore)
                if(option._controller.mapOptionValue()){
                    option._controller.initMinMax(option); // hacky init min/max again if value changed
                }
            }
        }
    }


    return html`
         <input type="hidden" name="${optionName(option)}" id="${option.id}" .value=${live(option.value)} form=${ifDefined(config.productFormLink)}  />
         <input type="date" class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}-date" @change=${(e) => option._controller.handleChange(e, option)}
            ?required=${option.required} min=${ifDefined(option.params.minDate != null ? option.params.minDate : undefined)} max=${ifDefined(option.params.maxDate != null ? option.params.maxDate : undefined)}>
    `;
}